import React, { useState, useEffect, useCallback } from "react";
import {
    Card,
    Select,
    DatePicker,
    Button,
    Table,
    Space,
    Tooltip,
    notification,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useRecurringMemberItemsService } from "../../../services/recurring/RecurringMemberItemsServiceProvider";
import { useClient } from "../../../common/ClientProvider";
import { useMember } from "../../../common/member/MemberContext";
import {
    RecurringMemberItemCatalog,
    RecurringMemberItem,
    AddMemberRecurringItemsRequest,
} from "../../../model/recurring/RecurringMemberItems";
import CreateRecurringMemberItemModal from "./CreateRecurringMemberItemModal";
import { PaymentPermissions, AdminPermissions, MemberPermissions } from "../../../model/auth/Permissions";
import { isOpenEndedDate } from "../../../utils/recurringItemValidity";
import { useFormatCurrency } from "../../../hooks/useFormatters";

interface RecurringMemberItemsPanelProps {
    membershipId: string;
    backgroundColor?: string;
    borderColor?: string;
}

const formatDate = (d: Date | Dayjs | undefined): string | undefined => {
    if (!d) return undefined;
    return dayjs(d).format("YYYY-MM-DD");
};

const toOptionalValidTo = (d: Date | undefined): Date | undefined =>
    d && !isOpenEndedDate(d) ? d : undefined;

/**
 * A comparable snapshot of the table, so Save can tell an actual edit from a reload.
 * Without it, "enabled whenever there is something to save" is the only rule available,
 * and that rule can't distinguish "no items, nothing changed" from "the last item was
 * just deleted" — which is why deleting the last item used to be unsaveable.
 */
const snapshotItems = (items: RecurringMemberItem[]): string =>
    JSON.stringify(
        items.map((row) => [row.item.id, formatDate(row.validFrom), formatDate(toOptionalValidTo(row.validTo))])
    );

const RecurringMemberItemsPanel: React.FC<RecurringMemberItemsPanelProps> = ({
    membershipId,
    backgroundColor = "#ffffff",
    borderColor = "#e5e7eb",
}) => {
    const recurringService = useRecurringMemberItemsService();
    const { currentClientId } = useClient();
    const formatCurrency = useFormatCurrency();
    const { permissions } = useMember();

    const [catalog, setCatalog] = useState<RecurringMemberItemCatalog[]>([]);
    const [memberItems, setMemberItems] = useState<RecurringMemberItem[]>([]);
    const [savedSnapshot, setSavedSnapshot] = useState<string>(snapshotItems([]));
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [selectedCatalogId, setSelectedCatalogId] = useState<number | null>(null);
    const [validFrom, setValidFrom] = useState<Dayjs>(dayjs());
    const [validTo, setValidTo] = useState<Dayjs | null>(null);

    // Every /members/dditems endpoint this panel calls is gated CanUpdateMember
    // (members:update), so the panel needs that too — a gate looser than the API's
    // shows a working-looking panel whose reads and saves all 403 (FORT-392/393).
    // admin:fortius bypasses every policy outright, so it stands on its own.
    const held = (name: string) => permissions?.permissions.some((p) => p.name === name) ?? false;
    const hasPermission =
        held(AdminPermissions.FortiusAdmin) ||
        (held(MemberPermissions.UpdateMember) &&
            (held(PaymentPermissions.ReadDirectDebits) || held(AdminPermissions.ClubAdmin)));

    const fetchCatalog = useCallback(async () => {
        try {
            const items = await recurringService.getCatalogItems();
            setCatalog(items);
        } catch (error) {
            console.error("Error fetching catalog:", error);
            notification.error({
                message: "Error",
                description: "Failed to load recurring items catalog",
            });
        }
    }, [recurringService]);

    const fetchMemberItems = useCallback(async () => {
        if (!membershipId) return;
        setLoading(true);
        try {
            const items = await recurringService.getMemberRecurringItems(membershipId);
            const normalized =
                (items ?? []).map((row) => ({
                    ...row,
                    validTo: toOptionalValidTo(row.validTo),
                }));
            setMemberItems(normalized);
            setSavedSnapshot(snapshotItems(normalized));
        } catch (error) {
            console.error("Error fetching member recurring items:", error);
            notification.error({
                message: "Error",
                description: "Failed to load member recurring items",
            });
            setMemberItems([]);
            setSavedSnapshot(snapshotItems([]));
        } finally {
            setLoading(false);
        }
    }, [membershipId, recurringService]);

    useEffect(() => {
        if (hasPermission) {
            fetchCatalog();
        }
    }, [hasPermission, fetchCatalog]);

    useEffect(() => {
        if (hasPermission && membershipId) {
            fetchMemberItems();
        }
    }, [hasPermission, membershipId, fetchMemberItems]);

    const selectedCatalogItem = selectedCatalogId
        ? catalog.find((c) => c.id === selectedCatalogId)
        : null;

    const alreadyHasItemId = selectedCatalogItem
        ? memberItems.some((m) => m.item.id === selectedCatalogItem.id)
        : false;

    const handleAdd = () => {
        if (!selectedCatalogItem || !currentClientId) return;
        if (alreadyHasItemId) {
            notification.warning({
                message: "Duplicate item",
                description: "This item is already in the list. Remove it first to change dates, or choose another item.",
            });
            return;
        }
        const newItem: RecurringMemberItem = {
            item: { ...selectedCatalogItem },
            validFrom: validFrom.toDate(),
            validTo: validTo ? validTo.toDate() : undefined,
        };
        setMemberItems((prev) => [...prev, newItem]);
        setSelectedCatalogId(null);
        setValidFrom(dayjs());
        setValidTo(null);
    };

    const handleRemove = (index: number) => {
        setMemberItems((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!currentClientId) {
            notification.error({
                message: "Error",
                description: "No client selected",
            });
            return;
        }
        setSaving(true);
        try {
            const seenIds = new Set<number>();
            const requests: AddMemberRecurringItemsRequest[] = memberItems
                .filter((row) => {
                    if (seenIds.has(row.item.id)) return false;
                    seenIds.add(row.item.id);
                    return true;
                })
                .map((row) => ({
                    clientName: currentClientId,
                    membershipId,
                    item: row.item,
                    validFrom: formatDate(row.validFrom)!,
                    validTo: formatDate(toOptionalValidTo(row.validTo)),
                }));
            // An empty list can't go through the replace-all save: with no rows there is no
            // membership id in the payload, so the API has nothing to apply the change to and
            // the member keeps the items the operator just deleted.
            if (requests.length === 0) {
                await recurringService.removeAllItemsFromMember(membershipId);
            } else {
                await recurringService.addItemsToMember(requests);
            }
            await fetchMemberItems();
        } catch (error) {
            console.error("Error saving recurring items:", error);
            notification.error({
                message: "Error",
                description: "Failed to save recurring items",
            });
        } finally {
            setSaving(false);
        }
    };

    const updateRowDates = (index: number, field: "validFrom" | "validTo", value: Dayjs | null) => {
        setMemberItems((prev) =>
            prev.map((row, i) => {
                if (i !== index) return row;
                const next = { ...row };
                if (field === "validFrom" && value) next.validFrom = value.toDate();
                if (field === "validTo") next.validTo = value ? value.toDate() : undefined;
                return next;
            })
        );
    };

    // Deleting the last row is an edit like any other, so Save follows what changed
    // rather than whether anything is left in the table.
    const isDirty = snapshotItems(memberItems) !== savedSnapshot;

    if (!hasPermission) {
        return null;
    }

    const columns = [
        {
            title: "Item",
            dataIndex: ["item", "itemName"],
            key: "itemName",
            render: (_: unknown, record: RecurringMemberItem) => record.item.itemName,
        },
        {
            title: "Price",
            key: "price",
            width: 100,
            render: (_: unknown, record: RecurringMemberItem) =>
                formatCurrency(record.item.price),
        },
        {
            title: "Valid from",
            key: "validFrom",
            width: 160,
            render: (_: unknown, record: RecurringMemberItem, index: number) => (
                <DatePicker
                    value={dayjs(record.validFrom)}
                    onChange={(d) => d && updateRowDates(index, "validFrom", d)}
                    format="DD MMM YYYY"
                    allowClear={false}
                />
            ),
        },
        {
            // Exclusive: a run collecting on this date does not charge the item. Named
            // "Stops on" rather than "Valid to" so the boundary is not a guess.
            title: "Stops on",
            key: "validTo",
            width: 160,
            render: (_: unknown, record: RecurringMemberItem, index: number) => (
                <DatePicker
                    value={toOptionalValidTo(record.validTo) ? dayjs(record.validTo) : null}
                    onChange={(d) => updateRowDates(index, "validTo", d)}
                    format="DD MMM YYYY"
                    allowClear
                />
            ),
        },
        {
            title: "",
            key: "action",
            width: 80,
            render: (_: unknown, __: RecurringMemberItem, index: number) => (
                <Button
                    type="text"
                    danger
                    aria-label={`Remove ${__.item.itemName}`}
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemove(index)}
                />
            ),
        },
    ];

    const selectOptions = catalog.map((c) => ({
        label: `${c.itemName} — ${formatCurrency(c.price)}`,
        value: c.id,
    }));

    return (
        <>
            <Card
                title="Recurring Member Items"
                className="mb-4"
                style={{ backgroundColor, borderColor }}
            >
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Space wrap align="center">
                        <Select<number>
                            placeholder="Select item to add"
                            style={{ minWidth: 220 }}
                            value={selectedCatalogId}
                            onChange={setSelectedCatalogId}
                            options={selectOptions}
                            allowClear
                        />
                        <DatePicker
                            value={validFrom}
                            onChange={(d) => d && setValidFrom(d)}
                            format="DD MMM YYYY"
                            allowClear={false}
                        />
                        <Tooltip title="The last collection is the run BEFORE this date — an item that stops on a collection date is not charged by it.">
                            <DatePicker
                                placeholder="Stops on (optional)"
                                value={validTo}
                                onChange={setValidTo}
                                format="DD MMM YYYY"
                                allowClear
                            />
                        </Tooltip>
                        <Button
                            type="default"
                            icon={<PlusOutlined />}
                            onClick={handleAdd}
                            disabled={!selectedCatalogItem || alreadyHasItemId}
                        >
                            Add
                        </Button>
                        <Button type="link" onClick={() => setCreateModalVisible(true)}>
                            Create new item
                        </Button>
                    </Space>
                    <Table
                        dataSource={memberItems}
                        columns={columns}
                        onRow={(record) => ({
                            'data-testid': `recurring-item-row-${record.item.id}`,
                        } as React.HTMLAttributes<HTMLElement>)}
                        rowKey={(_, i) => String(i)}
                        pagination={false}
                        loading={loading}
                        size="small"
                        scroll={{ x: 'max-content' }}
                    />
                    <Button
                        type="primary"
                        data-testid="recurring-items-save"
                        onClick={handleSave}
                        loading={saving}
                        disabled={!isDirty}
                    >
                        Save
                    </Button>
                </Space>
            </Card>
            <CreateRecurringMemberItemModal
                visible={createModalVisible}
                onClose={() => setCreateModalVisible(false)}
                onSuccess={fetchCatalog}
                clientName={currentClientId ?? ""}
            />
        </>
    );
};

export default RecurringMemberItemsPanel;
